'use strict'

import {
  PORTAL_PRESETS
} from './portal-presets.js'
import {
  getMissionAltitudeContextFor3d,
  syncPointAltitudeFields
} from './portal-altitude-utils.js'
import {
  setByPath
} from './portal-waypoint-core-utils.js'
import {
  xmlToObj,
  updatePointXmlByPath,
  updateXmlByPath
} from './portal-point-map-utils.js'
import {
  getPointName
} from './portal-import-state-utils.js'
import {
  startPortalLoadingTask
} from './portal-loading-widget.js'

const LOCAL_DEM_PROVIDER_KEY = 'localDEM'
const LOCAL_TERRAIN_STATE_KEY = 'PORTAL_LOCAL_TERRAIN_STATE'

const isHeightToolDebugEnabled = function () {
  return typeof window !== 'undefined' && window.PORTAL_DEBUG_HEIGHT_TOOL === true
}

const toFiniteHeightToolDebugNumberOrNull = function (value) {
  if (value === null || value === undefined) return null
  const numericValue = Number(value)
  return Number.isFinite(numericValue) ? numericValue : null
}

const logHeightToolDebug = function (channel, payload) {
  if (!isHeightToolDebugEnabled()) return
  if (typeof console === 'undefined' || typeof console.log !== 'function') return
  const messagePayload = {
    channel: String(channel || '').trim(),
    ...(payload && typeof payload === 'object' ? payload : {})
  }
  try {
    console.log(`HEIGHT_TOOL_FLOW_DEBUG ${JSON.stringify(messagePayload)}`)
  } catch (err) {
    console.log('HEIGHT_TOOL_FLOW_DEBUG {"channel":"debug_log_stringify_failed"}')
  }
}

const logHeightToolDebugHintOnce = function () {
  if (typeof window === 'undefined') return
  if (window.PORTAL_HEIGHT_TOOL_DEBUG_HINT_LOGGED === true) return
  if (typeof console === 'undefined' || typeof console.log !== 'function') return
  window.PORTAL_HEIGHT_TOOL_DEBUG_HINT_LOGGED = true
  console.log('PORTAL_DEBUG_HEIGHT_TOOL available. Enable with: window.PORTAL_DEBUG_HEIGHT_TOOL = true')
}

logHeightToolDebugHintOnce()

const HEIGHT_TOOL_POINT_HEIGHT_PATH_ALIASES = [
  'Point.height',
  'wpml:height',
  'height',
  'template.wpml:height',
  'waylines.wpml:height'
]

const HEIGHT_TOOL_POINT_EXECUTE_HEIGHT_PATH_ALIASES = [
  'Point.executeHeight',
  'wpml:executeHeight',
  'executeHeight',
  'template.wpml:executeHeight',
  'waylines.wpml:executeHeight'
]

const HEIGHT_TOOL_POINT_ELLIPSOID_HEIGHT_PATH_ALIASES = [
  'wpml:ellipsoidHeight',
  'ellipsoidHeight',
  'template.wpml:ellipsoidHeight',
  'waylines.wpml:ellipsoidHeight'
]

  const escapeHtmlLite = function (raw) {
    return String(raw === null || raw === undefined ? '' : raw)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
  }

  const buildHeightToolMissionState = function (points) {
    const list = Array.isArray(points) ? points.filter(Boolean) : []
    const firstPoint = list[0] || null
    const templatePlacemark = firstPoint && firstPoint.xmlEl ? firstPoint.xmlEl : null
    const templateXmlNode = firstPoint && firstPoint.xmlEl ? firstPoint.xmlEl.ownerDocument : null
    const templateFolderEl = templatePlacemark && templatePlacemark.parentElement ? templatePlacemark.parentElement : null
    const firstLinkedPlacemark = firstPoint && Array.isArray(firstPoint.linkedXmlEls) && firstPoint.linkedXmlEls[0]
      ? firstPoint.linkedXmlEls[0]
      : null
    const waylinesXmlNode = firstPoint && Array.isArray(firstPoint.linkedXmlEls) && firstPoint.linkedXmlEls[0]
      ? firstPoint.linkedXmlEls[0].ownerDocument
      : null
    const waylinesFolderEl = firstLinkedPlacemark && firstLinkedPlacemark.parentElement ? firstLinkedPlacemark.parentElement : null
    return {
      waypoints: list,
      xmlDoc: templateXmlNode,
      documentEl: templateXmlNode ? (templateXmlNode.getElementsByTagName('Document')[0] || templateXmlNode.documentElement) : null,
      folderEl: templateFolderEl,
      secondaryXmlDoc: waylinesXmlNode,
      secondaryDocumentEl: waylinesXmlNode ? (waylinesXmlNode.getElementsByTagName('Document')[0] || waylinesXmlNode.documentElement) : null,
      secondaryFolderEl: waylinesFolderEl
    }
  }

  const buildHeightToolAltitudeSyncContext = function (points, deps) {
    const missionState = buildHeightToolMissionState(points)
    return {
      importMissionState: missionState,
      DEM_PROVIDER_PRIORITY: [],
      ALTITUDE_RULES: PORTAL_PRESETS.altitudeRules,
      POINT_HEIGHT_PATH_ALIASES: HEIGHT_TOOL_POINT_HEIGHT_PATH_ALIASES,
      POINT_EXECUTE_HEIGHT_PATH_ALIASES: HEIGHT_TOOL_POINT_EXECUTE_HEIGHT_PATH_ALIASES,
      POINT_ELLIPSOID_HEIGHT_PATH_ALIASES: HEIGHT_TOOL_POINT_ELLIPSOID_HEIGHT_PATH_ALIASES,
      NON_EXPORT_POINT_HEIGHT_PATH: 'project.height',
      fnIsDemProviderEnabled: typeof deps.fnIsDemProviderEnabled === 'function' ? deps.fnIsDemProviderEnabled : function () { return true },
      fnXmlToObj: xmlToObj,
      fnSetByPath: setByPath,
      fnUpdatePointXmlByPath: function (point, path, value) {
        return updatePointXmlByPath(point, path, value, function (xmlNode, xmlPath, xmlValue) {
          return updateXmlByPath(xmlNode, xmlPath, xmlValue, function () {})
        })
      },
      fnTemplateSupportsPointCoordinatesField: function () { return false }
    }
  }

  const getHeightToolPointName = function (point, rowIndex, fnTF) {
    const name = String(getPointName(point) || '').trim()
    if (name) return name
    const safeIndex = Number.isFinite(Number(rowIndex)) ? (Number(rowIndex) + 1) : 0
    return fnTF('height_tool.point_name_fallback', { index: safeIndex })
  }

  const runPointHeightDemOverwriteFlow = async function (options, {
    _ = window._,
    $ = window.jQuery,
    Swal = window.Swal,
    areaSwal = Swal,
    fnGetPointHeightToolFieldMeta,
    fnIsDemProviderEnabled,
    fnT,
    fnTF,
    toFiniteNumber: fnToFiniteNumber,
    fnGetPointNumericByPathAliases,
    fnGetDemProviderLabel,
    fnFetchSurfaceElevationByProvider,
    fnSetPointDemValue,
    fnApplyEditsToPoints,
    DEM_PROVIDER_CONFIG = {},
    NON_EXPORT_POINT_HEIGHT_PATH,
  } = {}) {
    const fnIsLocalProviderActive = function () {
      if (typeof window === 'undefined') return false
      const state = window[LOCAL_TERRAIN_STATE_KEY]
      return !!(
        state &&
        state.active === true &&
        (
          typeof state.sampleElevation === 'function' ||
          typeof state.sampleElevationAsync === 'function' ||
          typeof state.sampleElevations === 'function'
        )
      )
    }
    const fnIsProviderAvailable = function (providerKey) {
      const key = String(providerKey || '').trim()
      if (!key) return false
      if (key === LOCAL_DEM_PROVIDER_KEY) return fnIsLocalProviderActive()
      return fnIsDemProviderEnabled(key)
    }

    const opts = _.assign({
      points: [],
      targetPath: '',
      providerKey: '',
      onApplied: null
    }, options || {})
    const points = Array.isArray(opts.points) ? opts.points.filter((pt) => !!pt) : []
    const targetPath = String(opts.targetPath || '').trim()
    const providerKey = String(opts.providerKey || '').trim()
    const fieldMeta = fnGetPointHeightToolFieldMeta(targetPath)
    if (!points.length || !fieldMeta || !providerKey) return false
    if (!fnIsProviderAvailable(providerKey)) {
      Swal.fire(fnT('dem.unknown_provider'))
      return false
    }

    const projectHeightByPoint = new Map()
    const missingProjectHeightPoints = []
    points.forEach((pt) => {
      const n = fnToFiniteNumber(_.get(pt, 'props.' + NON_EXPORT_POINT_HEIGHT_PATH))
      if (n === null || Number(n) === 0) {
        missingProjectHeightPoints.push(pt)
        return
      }
      projectHeightByPoint.set(pt, Math.round(n * 10) / 10)
    })

    let projectHeightSetManually = false
    let $projectHeightInput = $()
    const fillResult = await areaSwal.fire({
      title: fnT('common.warning'),
      html: [
        '<div class="text-start">',
        '<div>' + escapeHtmlLite(fnTF('height_tool.confirm_overwrite', { field: fieldMeta.leaf })) + '</div>',
        '<div class="small text-muted mt-2">' + escapeHtmlLite(fnTF('height_tool.confirm_formula', { provider: providerKey })) + '</div>',
        missingProjectHeightPoints.length
          ? ('<div class="mt-3">' + escapeHtmlLite(fnT('height_tool.project_height_empty')) + '</div>')
          : '',
        '<div class="mt-1">' + escapeHtmlLite(fnTF('height_tool.project_height_prompt', { field: fieldMeta.leaf })) + '</div>',
        '<label class="form-label form-label-sm mt-2" for="height-tool-project-height">' + escapeHtmlLite(fnT('height_tool.project_height_input_label')) + '</label>',
        '<input id="height-tool-project-height" data-height-tool-project-height type="number" step="0.1" class="form-control form-control-sm">',
        '</div>'
      ].join(''),
      showCancelButton: true,
      confirmButtonText: fnT('common.ok'),
      cancelButtonText: fnT('common.cancel'),
      didOpen: function (modal) {
        $projectHeightInput = $(modal).find('[data-height-tool-project-height]').first()
      },
      preConfirm: function () {
        const rawText = String($projectHeightInput.val() || '').trim()
        if (!rawText) return null
        const raw = Number(rawText)
        if (!Number.isFinite(raw)) {
          Swal.showValidationMessage(fnT('validate.altitude_number'))
          return false
        }
        return Math.round(raw * 10) / 10
      }
    })
    if (!fillResult.isConfirmed) return false
    const typedProjectHeight = fnToFiniteNumber(fillResult.value)
    if (typedProjectHeight !== null) {
      const typedRounded = Math.round(typedProjectHeight * 10) / 10
      points.forEach((pt) => {
        projectHeightByPoint.set(pt, typedRounded)
      })
      projectHeightSetManually = true
    }

    if (points.some((pt) => !projectHeightByPoint.has(pt))) {
      Swal.fire(fnT('height_tool.project_height_still_empty'))
      return false
    }

    const invalidCoord = points.find((pt) => {
      const lat = parseFloat(pt && pt.latlng && pt.latlng[0])
      const lng = parseFloat(pt && pt.latlng && pt.latlng[1])
      return !Number.isFinite(lat) || !Number.isFinite(lng)
    })
    if (invalidCoord) {
      Swal.fire(fnT('height_tool.invalid_coords'))
      return false
    }

    const fetchLoadingTask = startPortalLoadingTask({
      title: fnTF('height_tool.loading', { provider: providerKey }),
      status: fnT('dem.popup.starting'),
      progress: 0,
      mode: 'determinate'
    })

    const batchSize = (providerKey === LOCAL_DEM_PROVIDER_KEY)
      ? 200
      : Math.max(1, parseInt(((DEM_PROVIDER_CONFIG[providerKey] || {}).batchSize), 10) || 50)
    const rows = []
    let requestError = null
    try {
      let processed = 0
      for (let i = 0; i < points.length; i += batchSize) {
        const batch = points.slice(i, i + batchSize)
        const elevations = await fnFetchSurfaceElevationByProvider(providerKey, batch, {
          includeSourceProvider: true
        })
        logHeightToolDebug('fetch_batch_result', {
          providerKey: providerKey,
          targetPath: targetPath,
          batchStart: i,
          batchSize: batch.length,
          elevations: elevations.map(function (row, idx) {
            const batchPoint = batch[idx]
            return {
              idx: i + idx,
              lat: Number(batchPoint && batchPoint.latlng && batchPoint.latlng[0]),
              lng: Number(batchPoint && batchPoint.latlng && batchPoint.latlng[1]),
              elevation: toFiniteHeightToolDebugNumberOrNull(row && row.elevation),
              sourceProvider: String((row && row.provider) || '').trim()
            }
          })
        })
        batch.forEach((pt, idx) => {
          const elevationRow = elevations[idx]
          const dem = fnToFiniteNumber(elevationRow && typeof elevationRow === 'object' ? elevationRow.elevation : elevationRow)
          if (dem === null) return
          const demProviderKey = String(
            (elevationRow && typeof elevationRow === 'object' && elevationRow.provider)
              ? elevationRow.provider
              : providerKey
          ).trim() || providerKey
          const demRounded = Math.round(dem * 10) / 10
          const projectHeight = projectHeightByPoint.get(pt)
          const nextHeight = Math.round((demRounded + projectHeight) * 10) / 10
          rows.push({
            point: pt,
            projectHeight: projectHeight,
            demProviderKey: demProviderKey,
            dem: demRounded,
            nextHeight: nextHeight
          })
        })
        logHeightToolDebug('fetch_batch_rows', {
          providerKey: providerKey,
          targetPath: targetPath,
          batchStart: i,
          rows: batch.map(function (pt, idx) {
            const elevationRow = elevations[idx]
            const dem = fnToFiniteNumber(elevationRow && typeof elevationRow === 'object' ? elevationRow.elevation : elevationRow)
            const pointProjectHeight = projectHeightByPoint.get(pt)
            return {
              idx: i + idx,
              pointName: getHeightToolPointName(pt, i + idx, fnTF),
              lat: Number(pt && pt.latlng && pt.latlng[0]),
              lng: Number(pt && pt.latlng && pt.latlng[1]),
              projectHeight: Number.isFinite(Number(pointProjectHeight)) ? Number(pointProjectHeight) : null,
              dem: dem,
              demProviderKey: String((elevationRow && elevationRow.provider) || providerKey || '').trim(),
              nextHeight: (dem !== null && Number.isFinite(Number(pointProjectHeight)))
                ? Math.round((Math.round(Number(dem) * 10) / 10 + Number(pointProjectHeight)) * 10) / 10
                : null
            }
          })
        })
        processed += batch.length
        fetchLoadingTask.update({
          progress: points.length > 0 ? ((processed / points.length) * 100) : 100,
          status: providerKey + ' feldolgozva: ' + processed + ' / ' + points.length
        })
      }
    } catch (error) {
      requestError = error
    } finally {
      if (requestError) {
        fetchLoadingTask.fail({
          status: String((requestError && requestError.message) || fnT('dem.request_failed'))
        })
      } else {
        fetchLoadingTask.complete({
          progress: 100,
          status: providerKey + ' sikeres'
        })
      }
    }

    if (requestError) {
      Swal.fire(fnT('common.error'), String((requestError && requestError.message) || fnT('dem.request_failed')), 'error')
      return false
    }

    if (rows.length !== points.length) {
      Swal.fire(fnT('height_tool.dem_missing'))
      return false
    }

    const headerSuffix = projectHeightSetManually
      ? (' (' + fnT('height_tool.manual_value_suffix') + ')')
      : ''
    const tableHtml = [
      '<div class="table-responsive text-start">',
      '<table class="table table-sm table-striped align-middle mb-0">',
      '<thead><tr>',
      '<th>' + escapeHtmlLite(fnT('height_tool.col_point_name')) + '</th>',
      '<th>' + escapeHtmlLite(fnT('height_tool.col_project_height') + headerSuffix) + '</th>',
      '<th>' + escapeHtmlLite(fnT('height_tool.col_dem_provider')) + '</th>',
      '<th>' + escapeHtmlLite(fnT('height_tool.col_dem_value')) + '</th>',
      '<th>' + escapeHtmlLite(fieldMeta.leaf) + '</th>',
      '</tr></thead>',
      '<tbody>',
      rows.map((row, rowIndex) => {
        const demProviderLabel = typeof fnGetDemProviderLabel === 'function'
          ? fnGetDemProviderLabel(row.demProviderKey)
          : row.demProviderKey
        return '<tr>' +
          '<td>' + escapeHtmlLite(getHeightToolPointName(row.point, rowIndex, fnTF)) + '</td>' +
          '<td>' + escapeHtmlLite(row.projectHeight) + '</td>' +
          '<td>' + escapeHtmlLite(row.demProviderKey + ' - ' + demProviderLabel) + '</td>' +
          '<td>' + escapeHtmlLite(row.dem) + '</td>' +
          '<td>' + escapeHtmlLite(row.nextHeight) + '</td>' +
        '</tr>'
      }).join(''),
      '</tbody>',
      '</table>',
      '</div>'
    ].join('')

    const targetPathLower = String(targetPath || '').toLowerCase()
    let $previewPopup = $()
    let previewLoadingTask = null
    const fnYieldToUi = function () {
      return new Promise((resolve) => {
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(function () {
            resolve()
          })
          return
        }
        setTimeout(resolve, 0)
      })
    }
    const fnSetPreviewUiBusy = function (active) {
      if (!$previewPopup || !$previewPopup.length) return
      $previewPopup.find('button, input, select, textarea').prop('disabled', !!active)
    }
    const fnRenderPreviewProgress = function (current, total, isActive, textKey) {
      const safeTotal = Math.max(0, Number(total) || 0)
      const safeCurrent = Math.max(0, Math.min(safeTotal, Number(current) || 0))
      const percent = safeTotal > 0 ? Math.max(0, Math.min(100, Math.round((safeCurrent / safeTotal) * 100))) : 0
      const progressLabel = textKey
        ? fnTF(textKey, {
            current: safeCurrent,
            total: safeTotal,
            percent: percent
          })
        : ''
      if (!previewLoadingTask && (isActive || safeTotal > 0)) {
        previewLoadingTask = startPortalLoadingTask({
          title: fnT('height_tool.preview_title'),
          status: progressLabel,
          progress: percent,
          mode: 'determinate'
        })
      }
      if (!previewLoadingTask) return
      if (!isActive && safeTotal === 0) {
        previewLoadingTask.remove()
        previewLoadingTask = null
        return
      }
      if (isActive) {
        previewLoadingTask.update({
          status: progressLabel,
          progress: percent,
          mode: 'determinate'
        })
        return
      }
      previewLoadingTask.complete({
        status: progressLabel,
        progress: percent
      })
      previewLoadingTask = null
    }
    const fnApplyPreviewRowsWithProgress = async function () {
      const total = rows.length
      if (!total) {
        fnRenderPreviewProgress(0, 0, false, 'bulk.progress_idle')
        return {
          count: 0,
          targetPath: targetPath,
          providerKey: providerKey
        }
      }
      const chunkSize = Math.max(5, Math.min(20, Math.ceil(total / 40)))
      fnRenderPreviewProgress(0, total, true, 'bulk.progress_running')
      fnSetPreviewUiBusy(true)
      await fnYieldToUi()
      try {
        for (let startIdx = 0; startIdx < total; startIdx += chunkSize) {
          const chunk = rows.slice(startIdx, Math.min(total, startIdx + chunkSize))
          chunk.forEach((row) => {
            fnSetPointDemValue(row.point, row.demProviderKey, row.dem)
            const edits = {}
            edits[NON_EXPORT_POINT_HEIGHT_PATH] = row.projectHeight
            edits[targetPath] = row.nextHeight
            fnApplyEditsToPoints([row.point], edits)
            const syncContext = buildHeightToolAltitudeSyncContext([row.point], {
              fnIsDemProviderEnabled: fnIsDemProviderEnabled
            })
            const missionAltContext = getMissionAltitudeContextFor3d(syncContext.importMissionState, syncContext)
            syncPointAltitudeFields(row.point, {
              sourceMissionAltContext: missionAltContext,
              targetMissionAltContext: missionAltContext,
              absoluteAltitudeOverride: (targetPathLower === 'coordinates.alt' || targetPathLower.includes('ellipsoidheight')) ? row.nextHeight : null,
              sourceTemplateHeightOverride: targetPathLower.includes('executeheight') ? null : row.nextHeight,
              sourceExecuteHeightOverride: targetPathLower.includes('executeheight') ? row.nextHeight : null
            }, syncContext)
          })
          fnRenderPreviewProgress(Math.min(total, startIdx + chunk.length), total, true, 'bulk.progress_running')
          await fnYieldToUi()
        }
        fnRenderPreviewProgress(total, total, false, 'bulk.progress_done')
      } finally {
        fnSetPreviewUiBusy(false)
      }
      return {
        count: total,
        targetPath: targetPath,
        providerKey: providerKey
      }
    }

    const previewResult = await areaSwal.fire({
      title: fnT('height_tool.preview_title'),
      html: tableHtml,
      size: 'xl',
      width: '98vw',
      autoSize: false,
      showCancelButton: true,
      confirmButtonText: fnT('common.save'),
      cancelButtonText: fnT('common.cancel'),
      didOpen: function (modal) {
        $previewPopup = $(modal)
        fnRenderPreviewProgress(0, 0, false, 'bulk.progress_idle')
      },
      preConfirm: async function () {
        try {
          return await fnApplyPreviewRowsWithProgress()
        } catch (error) {
          areaSwal.showValidationMessage(String((error && error.message) || fnT('common.error')))
          fnRenderPreviewProgress(0, 0, false, 'bulk.progress_idle')
          return false
        }
      }
    })
    if (previewLoadingTask) {
      previewLoadingTask.remove()
      previewLoadingTask = null
    }
    if (!previewResult.isConfirmed) return false

    if (typeof opts.onApplied === 'function') {
      opts.onApplied(previewResult.value || { count: rows.length, targetPath: targetPath, providerKey: providerKey })
    }
    return true
  }

export {
  escapeHtmlLite,
  runPointHeightDemOverwriteFlow
}
